import { useEffect, useRef, useState } from "react";
import { Loader2, Send, Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import LessonInProgressBanner from "./LessonInProgressBanner";
import { refineLesson, resumeRefineJob } from "../lib/api";
import { clearPendingRefine, getPendingRefine } from "../lib/inProgress";

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const SUGGESTIONS = [
  "Make this 10 minutes shorter and keep the core skill focus.",
  "Add a French (FSL) version of the WALT and Success Criteria.",
  "Swap the example for one that connects to local Ontario geography.",
  "Add an explicit assessment-as-learning checkpoint mid-lesson.",
];

export const LessonRefineChat = ({ activity, onActivityUpdate }) => {
  const messages = activity?.refinement_messages || [];
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [resuming, setResuming] = useState(null); // { jobId, startedAt }
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, loading]);

  // Resume any in-flight refine job for this activity (tab refresh recovery).
  useEffect(() => {
    if (!activity?.id) return;
    const pending = getPendingRefine(activity.id);
    if (!pending?.jobId) return;
    let cancelled = false;
    setResuming(pending);
    setLoading(true);
    (async () => {
      try {
        const job = await resumeRefineJob(activity.id, pending.jobId);
        if (cancelled) return;
        if (job?.activity && onActivityUpdate) onActivityUpdate(job.activity);
        if (job?.lesson_updated) {
          toast.success("Lesson updated", {
            description: job.reply?.slice(0, 80),
          });
        }
      } catch (e) {
        if (cancelled) return;
        if (e?.response?.status === 404) {
          clearPendingRefine(activity.id);
        } else {
          toast.error("Could not finish your earlier refinement", {
            description: e?.response?.data?.detail || e?.message,
          });
        }
      } finally {
        if (!cancelled) {
          setResuming(null);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activity?.id, onActivityUpdate]);

  const submit = async (text) => {
    const trimmed = (text ?? input).trim();
    if (!trimmed || !activity?.id || loading) return;
    setLoading(true);
    if (text === undefined) setInput("");
    try {
      const job = await refineLesson(activity.id, trimmed);
      if (job?.activity && onActivityUpdate) onActivityUpdate(job.activity);
      if (job?.lesson_updated) {
        toast.success("Lesson updated", { description: job.reply?.slice(0, 80) });
      } else {
        toast(job?.reply?.slice(0, 80) || "Got it", { description: "(no structural change)" });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "Refinement failed";
      toast.error("Refinement failed", { description: String(detail) });
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      submit();
    }
  };

  return (
    <section
      data-testid="lesson-refine-chat"
      className="mt-10 border-t border-[#4a6984]/10 pt-8"
    >
      <div className="flex items-center gap-2 mb-5">
        <div className="h-9 w-9 rounded-lg bg-[#96ded1]/30 border border-[#96ded1]/60 grid place-items-center">
          <Wand2 className="h-4 w-4 text-[#1c2d3d]" />
        </div>
        <div>
          <p className="font-bold uppercase tracking-[0.25em] text-xs text-[#4a6984]/70">
            Refine This Lesson
          </p>
          <p className="text-sm text-[#4a6984]/85 font-medium">
            Ask for tweaks — shorter, French, different content focus, extra
            scaffolding, assessment checkpoints, anything.
          </p>
        </div>
      </div>

      {/* Resumed-from-refresh banner (only while polling a recovered job) */}
      {resuming && (
        <div className="mb-4">
          <LessonInProgressBanner
            variant="refine"
            startedAt={resuming.startedAt}
          />
        </div>
      )}

      {/* Thread */}
      <div
        ref={scrollRef}
        data-testid="refine-thread"
        className="rounded-xl border border-[#4a6984]/10 bg-[#f4f5f7]/60 p-4 md:p-5 max-h-[420px] overflow-y-auto"
      >
        {messages.length === 0 && !loading ? (
          <div className="text-center py-8">
            <Sparkles className="h-5 w-5 text-[#4a6984] mx-auto" />
            <p className="mt-2 text-sm text-[#4a6984]/80 font-medium">
              No refinement turns yet. Try a quick suggestion below.
            </p>
          </div>
        ) : (
          <ul className="space-y-3">
            {messages.map((m, i) => {
              const isUser = m.role === "user";
              return (
                <li
                  key={i}
                  data-testid={`refine-turn-${i}`}
                  className={`flex ${isUser ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm font-medium leading-relaxed ${
                      isUser
                        ? "bg-[#4a6984] text-white rounded-br-md"
                        : "bg-white border border-[#4a6984]/10 text-[#4a6984] rounded-bl-md"
                    }`}
                  >
                    <p className="whitespace-pre-wrap">{m.content}</p>
                    <div
                      className={`mt-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] ${
                        isUser ? "text-white/70" : "text-[#4a6984]/60"
                      }`}
                    >
                      <span>{isUser ? "You" : "Atomic Edu"}</span>
                      {m.created_at && <span>· {fmtTime(m.created_at)}</span>}
                      {m.lesson_updated && (
                        <span
                          className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-extrabold bg-[#96ded1]/40 text-[#1c2d3d]"
                          data-testid={`refine-turn-${i}-updated`}
                        >
                          <Sparkles className="h-2.5 w-2.5" /> Lesson updated
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
            {loading && (
              <li className="flex justify-start" data-testid="refine-loading">
                <div className="rounded-2xl rounded-bl-md px-4 py-3 bg-white border border-[#4a6984]/10 text-[#4a6984] inline-flex items-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  <span className="text-sm font-medium">Thinking…</span>
                </div>
              </li>
            )}
          </ul>
        )}
      </div>

      {/* Suggestion chips */}
      {messages.length === 0 && !loading && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="refine-suggestions">
          {SUGGESTIONS.map((s, i) => (
            <button
              key={i}
              data-testid={`refine-suggestion-${i}`}
              onClick={() => submit(s)}
              className="text-xs text-[#4a6984] hover:text-[#1c2d3d] font-semibold px-3 py-1.5 rounded-full border border-[#4a6984]/15 hover:border-[#4a6984]/35 bg-white"
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="mt-4 flex items-end gap-3">
        <Textarea
          data-testid="refine-input"
          rows={2}
          placeholder="e.g. Make the success criteria more measurable and add a French extension."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={loading}
          className="bg-white border border-[#4a6984]/20 text-[#4a6984] font-medium focus-visible:ring-2 focus-visible:ring-[#96ded1] rounded-md resize-none flex-1"
        />
        <Button
          onClick={() => submit()}
          disabled={loading || !input.trim()}
          data-testid="refine-send"
          className="ae-cta h-12 px-5 rounded-lg inline-flex items-center gap-2 shrink-0"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Send
        </Button>
      </div>
      <p className="mt-2 text-xs text-[#4a6984]/60">
        Tip: press <kbd className="px-1 py-0.5 rounded bg-[#f4f5f7] border border-[#4a6984]/15">⌘/Ctrl + Enter</kbd> to send.
      </p>
    </section>
  );
};

export default LessonRefineChat;
